import clsx from 'clsx';
import React from 'react';
import { TextColor } from '../../types';
import {
  Avatar,
  AvatarComponent,
  AvatarProps,
  IconComponent,
  Icons,
  IconsProps,
  Typography,
  TypographyType,
} from '../shared';

const LABEL_MAX_LINES = 2;

function isAvatarComponent(component: React.ReactNode): component is AvatarComponent {
  return React.isValidElement(component) && component.type === Avatar;
}

function isIconComponent(component: React.ReactNode): component is IconComponent {
  return React.isValidElement(component) && component.type === Icons;
}

type SidebarAvatarComponent = AvatarComponent<Pick<AvatarProps, 'imgSrc' | 'label'>>;
type SidebarIconComponent = IconComponent<Pick<IconsProps, 'icon' | 'color' | 'fillColor'>>;

export type SidebarButtonProps = {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  endElement?: SidebarAvatarComponent | SidebarIconComponent;
  startElement?: SidebarAvatarComponent;
  subLabel?: string;
};

const SidebarButton = React.forwardRef<HTMLDivElement, SidebarButtonProps>(
  ({ label, onClick, active, disabled, endElement, startElement, subLabel }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'flex min-h-8 cursor-pointer select-none items-center justify-between gap-2 rounded-[4px] px-4 py-1',
          active ? 'bg-neutral/20' : 'hover:bg-neutral/10',
          disabled && 'pointer-events-none opacity-40'
        )}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          {startElement}
          <div className="flex flex-col">
            <Typography maxLines={LABEL_MAX_LINES}>{label}</Typography>
            <Typography maxLines={LABEL_MAX_LINES} type={TypographyType.X_SMALL} color={TextColor.SECONDARY}>
              {subLabel}
            </Typography>
          </div>
        </div>
        {/* Render the end element */}
        {isAvatarComponent(endElement) && React.cloneElement(endElement, { size: 16, ...endElement.props })}
        {isIconComponent(endElement) &&
          React.cloneElement(endElement, { color: TextColor.SECONDARY, ...endElement.props })}
      </div>
    );
  }
);

SidebarButton.displayName = 'SidebarButton';

export default SidebarButton;
